import type { AxiosRequestConfig } from "axios";
import { legacyAxios } from "../bridges/apiClient";
import { toApiError } from "./errors";

const call = async <T>(config: AxiosRequestConfig): Promise<T> => {
  try {
    const res = await legacyAxios.request<T>({ ...config, nextLayer: true } as AxiosRequestConfig);
    return res.data;
  } catch (err) {
    throw toApiError(err);
  }
};

/** Server calls for the new layer: same instance (auth, 401/402/500, contact limit) as the old app. */
export const api = {
  get: <T>(url: string, params?: object) => call<T>({ method: "GET", url, params }),
  /** `quietServerError`: the caller shows its own sentence for a 500, so the old "Server error" toast (which can cover a phone sheet's Save button) stays away. */
  post: <T>(url: string, data?: unknown, config?: Pick<AxiosRequestConfig, "timeout" | "headers"> & { quietServerError?: boolean }) => call<T>({ ...config, method: "POST", url, data }),
  put: <T>(url: string, data?: unknown) => call<T>({ method: "PUT", url, data }),
  patch: <T>(url: string, data?: unknown) => call<T>({ method: "PATCH", url, data }),
  del: <T>(url: string, data?: unknown) => call<T>({ method: "DELETE", url, data }),
};

/**
 * A file upload (multipart) through the same instance and the same error mapping as `api.post`. No Content-Type of
 * our own: the browser writes multipart/form-data with its boundary. `onProgress` gets whole percents (0–100) while
 * the file goes up; nothing when the size is not known. `signal` stops the upload (the call then rejects).
 */
export const upload = <T>(url: string, form: FormData, onProgress?: (pct: number) => void, signal?: AbortSignal): Promise<T> =>
  call<T>({
    method: "POST",
    url,
    data: form,
    ...(signal ? { signal } : {}),
    ...(onProgress
      ? {
          onUploadProgress: (e: { loaded: number; total?: number }) => {
            if (e.total) onProgress(Math.min(100, Math.round((e.loaded * 100) / e.total)));
          },
        }
      : {}),
  });

/** File name from a Content-Disposition header: RFC 5987 `filename*=UTF-8''…` wins over `filename="…"`. */
export const filenameFromDisposition = (header?: string | null): string | null => {
  if (!header) return null;
  const star = /filename\*\s*=\s*(?:UTF-8|utf-8)''([^;]+)/i.exec(header);
  if (star) {
    try {
      return decodeURIComponent(star[1].trim());
    } catch {
      /* fall through to the plain form */
    }
  }
  const plain = /filename\s*=\s*(?:"((?:[^"\\]|\\.)*)"|([^;]+))/i.exec(header);
  if (!plain) return null;
  return (plain[1] !== undefined ? plain[1].replace(/\\(.)/g, "$1") : plain[2].trim()) || null;
};

const blobText = (b: Blob): Promise<string> =>
  typeof b.text === "function"
    ? b.text()
    : new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(String(r.result));
        r.onerror = () => reject(r.error);
        r.readAsText(b);
      });

/** A failed blob request carries its JSON error body as a Blob; read it back so `code` and field errors survive. */
const readBlobErrorBody = async (err: any): Promise<unknown> => {
  const res = err?.response;
  const type = String(res?.headers?.["content-type"] ?? res?.data?.type ?? "");
  if (res?.data instanceof Blob && type.includes("json")) {
    try {
      res.data = JSON.parse(await blobText(res.data));
    } catch {
      /* not JSON after all: leave it */
    }
  }
  return err;
};

/**
 * A file download through the same instance (auth, interceptors) as every other call. `quietServerError`: the caller says a
 * failed download in its own words, so the old "Server error" toast stays away (the default is unchanged).
 */
export const getBlob = async (url: string, params?: object, config?: { quietServerError?: boolean }): Promise<{ blob: Blob; filename: string | null }> => {
  try {
    const res = await legacyAxios.request<Blob>({ method: "GET", url, params, responseType: "blob", ...(config?.quietServerError ? { quietServerError: true } : {}) } as AxiosRequestConfig);
    return { blob: res.data, filename: filenameFromDisposition(res.headers?.["content-disposition"]) };
  } catch (err) {
    throw toApiError(await readBlobErrorBody(err));
  }
};
